import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/skeleton.default.js';
import * as wireframeSkin from './skins/skeleton.wireframe.js';

// Pre-compiled CSSStyleSheet instances — shared across all instances.
const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

const VALID_VARIANTS = ['text', 'circular', 'rectangular'];
const VALID_EFFECTS  = ['none', 'pulse', 'shimmer'];

/**
 * run-skeleton — loading-state placeholder element.
 *
 * Renders an animated (or static) shape that communicates "content is loading"
 * without requiring JavaScript to determine the final layout.  No form
 * participation. No interactive behaviour. aria-hidden on all output.
 *
 * @attr {string} variant    - Shape: 'text' (default) | 'circular' | 'rectangular'
 * @attr {string} effect     - Animation: 'pulse' (default) | 'shimmer' | 'none'
 * @attr {string} width      - CSS width of each line (default '100%')
 * @attr {string} height     - CSS height; empty = auto per variant
 * @attr {number} lines      - Number of text-line elements (text variant only, default 1)
 * @attr {string} skin       - Override skin: 'default' | 'wireframe'
 * @attr        data-reduced-motion - Presence suppresses all animation
 */
class RunSkeleton extends RunElement {
  #variant  = 'text';
  #effect   = 'pulse';
  #width    = '100%';
  #height   = '';
  #lines    = 1;
  #skin     = null;
  #rendering = false;

  static get observedAttributes() {
    return ['variant', 'effect', 'width', 'height', 'lines', 'skin', 'data-reduced-motion'];
  }

  constructor() {
    super();
  }

  // ── connectedCallback ──────────────────────────────────────────────────────

  connectedCallback() {
    super.connectedCallback();
    // vi-01, vi-02: host is never focusable, all content is aria-hidden
    this.setAttribute('aria-hidden', 'true');
    this.setAttribute('role', 'presentation');
    this.tabIndex = -1;
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  // ── Attribute → property sync ──────────────────────────────────────────────

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'variant':            this.#setVariant(newValue); break;
      case 'effect':             this.#setEffect(newValue); break;
      case 'width':              this.#setWidth(newValue); break;
      case 'height':             this.#setHeight(newValue); break;
      case 'lines':              this.#setLines(newValue); break;
      case 'skin':               this.#skin = newValue; this.requestUpdate(); break;
      case 'data-reduced-motion': this.requestUpdate(); break;
    }
  }

  // ── Properties ────────────────────────────────────────────────────────────

  get variant() { return this.#variant; }
  set variant(v) { this.#setVariant(v); }

  get effect() { return this.#effect; }
  set effect(v) { this.#setEffect(v); }

  get width() { return this.#width; }
  set width(v) { this.#setWidth(v); }

  get height() { return this.#height; }
  set height(v) { this.#setHeight(v); }

  get lines() { return this.#lines; }
  set lines(v) { this.#setLines(v); }

  get skin() { return this.#skin; }
  set skin(v) {
    const coerced = v == null ? null : String(v);
    if (this.#skin === coerced) return;
    this.#skin = coerced;
    coerced === null ? this.removeAttribute('skin') : this.setAttribute('skin', coerced);
    this.requestUpdate();
  }

  // ── Computed accessors used by skin render functions ───────────────────────

  /**
   * Resolved CSS height string.
   * - 'text'        → '1em'
   * - 'circular'    → mirrors width (square aspect, vi-04)
   * - 'rectangular' → '100px' default
   * An explicit `height` property always wins.
   */
  get _resolvedHeight() {
    if (this.#height) return this.#height;
    switch (this.#variant) {
      case 'circular':    return this.#width;    // vi-04: square when only width set
      case 'rectangular': return '100px';
      default:            return '1em';          // text
    }
  }

  get dataReducedMotion() { return this.hasAttribute('data-reduced-motion'); }

  /** vi-05: true when data-reduced-motion attribute is present */
  get _isReducedMotion() {
    return this.hasAttribute('data-reduced-motion');
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  render() {
    // Delegate entirely to skin modules.
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
    } finally {
      this.#rendering = false;
    }
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  #resolveSkin() {
    if (this.#skin) return this.#skin;
    return resolveSkinFromAncestors(this);
  }

  #setVariant(v) {
    const old = this.#variant;
    const coerced = VALID_VARIANTS.includes(v) ? v : 'text';
    this.#variant = coerced;
    // Always normalize the attribute to coerced value (handles invalid→default case
    // where old===coerced but the DOM attribute holds the invalid value).
    if (this.getAttribute('variant') !== coerced) {
      this.setAttribute('variant', coerced);
    }
    if (old !== coerced) {
      this.requestUpdate();
    }
  }

  #setEffect(v) {
    const old = this.#effect;
    const coerced = VALID_EFFECTS.includes(v) ? v : 'pulse';
    this.#effect = coerced;
    if (this.getAttribute('effect') !== coerced) {
      this.setAttribute('effect', coerced);
    }
    if (old !== coerced) {
      this.requestUpdate();
    }
  }

  #setWidth(v) {
    const next = (v != null && v !== '') ? String(v) : '100%';
    if (this.#width === next) return;
    this.#width = next;
    this.requestUpdate();
  }

  #setHeight(v) {
    const next = (v != null && v !== '') ? String(v) : '';
    if (this.#height === next) return;
    this.#height = next;
    this.requestUpdate();
  }

  #setLines(v) {
    const n = parseInt(v, 10);
    const next = Number.isFinite(n) && n >= 1 ? n : 1;
    if (this.#lines === next) return;
    this.#lines = next;
    this.requestUpdate();
  }
}

if (!customElements.get('run-skeleton')) {
  customElements.define('run-skeleton', RunSkeleton);
}

export { RunSkeleton };
export default RunSkeleton;
